import { Alert, Backdrop, CircularProgress, CssBaseline, Slide, Snackbar, ThemeProvider } from '@mui/material';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { BrowserRouter } from 'react-router-dom';
import ErrorBoundary from 'src/components/common/ErrorBoundary';
import { AppState, useAppSelector } from 'src/store';
import { useAppSelector as selector, useAppDispatch } from 'src/store/hooks';
import { getPoratlHost } from './components/hooks/useUtil';
import Layout from './components/layout/Layout';
import './muiLicense';
import { setPortalProps, setPreferences, setToken, setUser } from './store/User';
import { setSelectedPortalApp } from './store/selectedPortalApp';
import { theme } from './theme';
import AppRoutes from './routes/AppRoutes';
import Standalone from './components/common/Standalone';

const DEFAULT_EVENTS = [
  'mousemove',
  'keydown',
  'wheel',
  'DOMMouseScroll',
  'mousewheel',
  'mousedown',
  'touchstart',
  'touchmove',
  'MSPointerDown',
  'MSPointerMove',
  'visibilitychange'
];

const App: React.FC = () => {
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const { message, severity, timestamp } = useAppSelector((state: AppState) => state.settings.snackbar);
  const user = selector(state => state.user.user);
  const [waitForUserInfo, setUserInfoReceived] = useState(false);
  const dispatch = useAppDispatch();
  const sendEventToPortal = () => {
    window.parent.postMessage({ action: 'USER_INFO' }, getPoratlHost());
    setTimeout(() => {
      window.parent.postMessage({ action: 'SELECTED_APP' }, getPoratlHost());
      window.parent.postMessage({ action: 'PORTAL_PROPS' }, getPoratlHost());
    }, 300);
  };

  const sendIdleEventToPortal = (e: any) => {
    window.parent.postMessage({ action: 'UPDATE_IDLE_TIME' }, getPoratlHost());
    if (e === 'mousedown') {
      window.parent.postMessage({ action: 'UPDATE_ON_CLICK' }, getPoratlHost());
    }
  };

  useEffect(() => {
    if (user?.sponsor) {
      axios.interceptors.request.use((config: any) => {
        return {
          ...config,
          headers: {
            ...config.headers,
            sponsor: user?.sponsor_sponsor_name
          }
        };
      });
    }
  }, [user?.sponsor]);

  useEffect(() => {
    sendEventToPortal();
    window.addEventListener('message', handleEvent);
    DEFAULT_EVENTS.forEach((e: any) => {
      document.addEventListener(e, () => sendIdleEventToPortal(e), {
        capture: true,
        passive: true
      });
    });
    return () => {
      window.removeEventListener('message', handleEvent);
      DEFAULT_EVENTS.forEach((e: any) => {
        document.removeEventListener(e, sendIdleEventToPortal, {
          capture: true
        });
      });
    };
  }, []);

  useEffect(() => {
    message && setIsSnackbarOpen(true);
  }, [message, timestamp]);

  const changeFontSize = (increase = true) => {
    const htmlElement = document.querySelector('html') as any;
    const style = window.getComputedStyle(htmlElement, null).fontSize;
    htmlElement.style.fontSize = `${parseFloat(style) + (increase ? 1 : -1)}px`;
  };

  const handleEvent = (event: MessageEvent) => {
    if ((event?.data?.source || '').indexOf('react-devtools-') > -1) {
      return;
    }
    setUserInfoReceived(true);
    if (event.origin !== getPoratlHost()) {
      // console.info('Message received from incorrect application.');
    } else {
      const message = event.data;
      switch (message.action) {
        case 'SPELL_CHECK':
          changeFontSize(message.increase);
          break;
        case 'USER_INFO':
          dispatch(setUser(message.user));
          break;
        case 'UPDATE_TOKEN':
          dispatch(setToken(message.token));
          break;
        case 'PREFERENCES':
          dispatch(setPreferences(message.data));
          break;
        case 'PORTAL_PROPS':
          dispatch(setPortalProps(message.data));
          break;
        case 'SELECTED_APP':
          if (message && message.data) {
            dispatch(setSelectedPortalApp(message.data.selected_app.slug_id));
          } else if (message) {
            dispatch(setSelectedPortalApp(message.selected_app.slug_id));
          }
          break;
        default:
          console.debug('Message not found', message.action);
      }
    }
  };
  if (user === undefined || user === null) {
    return (
      <>
        {waitForUserInfo && <Standalone />}
        <Backdrop sx={{ color: '#fff', zIndex: theme => theme.zIndex.drawer + 1 }} open={!waitForUserInfo}>
          <CircularProgress />
        </Backdrop>
      </>
    );
  }
  return (
    <ThemeProvider theme={theme}>
      <DndProvider backend={HTML5Backend}>
        <CssBaseline />
        <BrowserRouter>
          <ErrorBoundary>
            <Layout>
              <AppRoutes />
            </Layout>
          </ErrorBoundary>
        </BrowserRouter>
      </DndProvider>
      <Snackbar
        open={isSnackbarOpen}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        autoHideDuration={5000}
        TransitionComponent={props => <Slide {...props} direction="left" />}
        onClose={(_event, reason) => {
          if (reason === 'timeout') {
            setIsSnackbarOpen(false);
          }
        }}
      >
        <Alert severity={severity}>{message}</Alert>
      </Snackbar>
    </ThemeProvider>
  );
};

export default App;
